import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/Badge';
import { ProfileCard, ProfileCardIcon } from './ProfileCard';
import { ROLE_PROFILES } from '../../data/profileConfig';
import { useTerminology } from '../../hooks/useTerminology';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Role-aware experience.
 *
 * One base profile, plus a role's own console — not a duplicated profile page per
 * role. Which roles a person may switch into is decided by the roles ON THEIR
 * IDENTITY, not by this list: a role they do not hold is shown as unavailable
 * rather than hidden, so the platform's shape is legible, and it cannot be
 * entered because the guard on that console refuses it.
 */
export function RoleAwareSection() {
  const {
    phrase
  } = useTerminology();
  const {
    identity,
    switchRole
  } = useAuth();
  const navigate = useNavigate();
  if (!identity) return null;
  return <ProfileCard className="p-5 sm:p-6" data-id="element-4582" data-mp="3h98jqaynppoz">
      <div data-id="element-4583" data-mp="3hcctroyszfnk">
        <h2 className="text-h4 font-semibold text-ink" data-id="element-4584" data-mp="27e237jdo21s7">Role-aware experience</h2>
        <p className="mt-1 max-w-[62ch] text-body-sm text-ink-muted" data-id="element-4585" data-mp="27fwmkt9ahlu6">
          Your profile adapts based on the roles you hold. Signing in proves who you are; your roles
          decide what you may do.
        </p>
      </div>

      <ul className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5" data-id="element-4586" data-mp="2ccn1hd9cybe0">
        {ROLE_PROFILES.map(profile => {
        const held = identity.roles.includes(profile.role);
        const active = identity.activeRole === profile.role;
        return <li key={profile.role} data-id="element-4587" data-mp="1jqal1z5g2uj0">
              <div className={`flex h-full flex-col rounded-xl border p-4 ${active ? 'border-brand-700 bg-brand-50/40' : 'border-line bg-surface'}`} data-id="element-4588" data-mp="1yd3wgtbufg5x">
                <ProfileCardIcon name={profile.icon} tone={active ? 'brand' : 'neutral'} data-id="element-4589" data-mp="15waz0zzi67cm" />
                <p className="mt-3 text-body font-semibold text-ink" data-id="element-4590" data-mp="10yhs24qdl9ba">{phrase(profile.label)}</p>
                <p className="mt-1 text-body-sm text-ink-muted" data-id="element-4591" data-mp="310r7w8zzs625">{phrase(profile.summary)}</p>
                <div className="mt-auto pt-3" data-id="element-4592" data-mp="35z2d26fcdse1">
                  {active ? <Badge tone="ok" data-id="element-4593" data-mp="3aq0y1jb9zenm">Active</Badge> : held ? <button type="button" onClick={() => {
                switchRole(profile.role);
                navigate(profile.to);
              }} className="text-body-sm font-medium text-brand-700 hover:underline" data-id="element-4594" data-mp="3as5bosyz7s13">
                      Switch role →
                    </button> : <span className="text-body-sm text-ink-subtle" data-id="element-4595" data-mp="2w14t3v4imhr8">Not on your account</span>}
                </div>
              </div>
            </li>;
      })}
      </ul>
    </ProfileCard>;
}